'use client'

import type { TransactionDetailsView } from '@mentora/api-public'
import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type { TransactionActions } from './transaction-actions-card'
import { Loader2 } from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { MembershipDetailsPanel } from './membership-details-panel'
import { TransactionDetailsPanel } from './transaction-details-panel'
import { transactionsApi } from './transactions-api'

// One transaction, loaded from apps/api and shown with its actions — the body
// of both the Transactions dialog and the details page. After an action it
// re-reads the transaction, and refreshes the route so the list behind it (or
// any other server-rendered figure) shows the change too: these actions are
// HTTP calls to the shared API, not server actions, so nothing revalidates
// the web app's pages for them.

type Kind = 'course' | 'event' | 'membership'

type State
  = | { status: 'loading' }
    | { status: 'ready', view: TransactionDetailsView }
    | { status: 'error', errorKey: string }

export function TransactionDetailsLoader({ kind, id, listCurrency }: {
  kind: Kind
  id: string
  /** The storefront's list currency (`usd`, `brl`). */
  listCurrency: string
}) {
  const t = useTranslations('transactions')
  const tRoot = useTranslations()
  const format = useFormatter()
  const router = useRouter()
  const [state, setState] = useState<State>({ status: 'loading' })

  const load = useCallback(async () => {
    const result = await transactionsApi.load({ kind, id })
    setState(result.ok && result.data ? { status: 'ready', view: result.data } : { status: 'error', errorKey: result.ok ? 'transactions.dialog.loadFailed' : result.errorKey })
  }, [kind, id])

  useEffect(() => {
    void load()
  }, [load])

  const reloading = <A extends unknown[], R extends ActionResult<unknown>>(action: (...args: A) => Promise<R>) =>
    async (...args: A): Promise<R> => {
      const result = await action(...args)
      if (result.ok) {
        await load()
        router.refresh()
      }
      return result
    }
  const actions: TransactionActions = {
    refund: reloading(transactionsApi.refund),
    cancel: reloading(transactionsApi.cancel),
    retryRefund: reloading(transactionsApi.retryRefund),
  }
  const formatPrice = (cents: number | null | undefined) => format.number((cents ?? 0) / 100, { style: 'currency', currency: listCurrency.toUpperCase() })

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-12" role="status" aria-label={t('dialog.loading')}>
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (state.status === 'error')
    return <p className="py-6 text-sm text-muted-foreground">{tRoot(state.errorKey)}</p>
  if (state.view.kind === 'membership') {
    return (
      <MembershipDetailsPanel
        page={state.view.page}
        cancelMembership={reloading(transactionsApi.cancelMembership)}
        retryRefund={actions.retryRefund}
      />
    )
  }
  return <TransactionDetailsPanel page={state.view.page} formatPrice={formatPrice} actions={actions} />
}
